import { ModalFactory, ModalObject } from '@vend/vend.ui'
import angular from 'angular'
import { ApiProduct } from '@vend/types'
import {
  InitialQuickProduct,
  VariantAttribute,
  VariantAttributeCombinationDetail,
} from '@vend/business-components'
import productModalTemplate from '../components/react/business-components/ProductModal/product-modal.html'
import addProductFastModalTemplate from '../components/product/add-product-fast-modal/add-product-fast-modal.html'
import { $q } from '../ng-compat/imports'
import { ProductsStore } from '../factories/database/products-store'
import { VariantPickerModalService } from '../components/product/variant-picker-modal/variant-picker-modal.service'

/**
 * Provides product related modals e.g. to allow the user to select a variant or view product information.
 *
 * @module productModals
 */
angular
  .module('webregister.modals')
  .factory('productModals', ProductModalsFactory)

export function ProductModalsFactory(
  productsStore: ProductsStore,
  Modal: ModalFactory,
  variantPickerModalService: VariantPickerModalService
) {
  /**
   * Provides product related modals e.g. to allow the user to select a variant or view product information.
   *
   * @class ProductModals
   * @static
   */
  return {
    // ============================== Public API ============================== //

    /**
     * Open the select variant modal.
     *
     * @method selectVariant
     *
     * @param {Object} options
     *
     *        @param {Product} options.parentProduct
     *                 The parent product
     *
     *        @param {Boolean} options.allowVariantParentSelect
     *                Used to determine if we should show the variant parent selector
     *
     *        @param {String} title
     *                Title to use when we allowVariantParentSelect
     *
     * @return {Promise<ProductVariant>} A promise resolved with the selected product variant.
     */
    selectVariant(options: {
      parentProduct: ApiProduct
      allowVariantParentSelect: boolean
      title: string
    }) {
      return $q((resolve, reject) => {
        productsStore
          .getAllVariantProducts(options.parentProduct)
          .then(variantProducts => {
            const variantSelectorOptions = {
              parentProduct: options.parentProduct,
              allowVariantParentSelect: options.allowVariantParentSelect,
              title: options.title,
              variantProducts,
            }

            return variantPickerModalService.openVariantPicker(
              variantSelectorOptions
            )
          })
          .then(productVariant => {
            if (productVariant) {
              resolve(productVariant)
            } else {
              reject()
            }
          }, reject)
      })
    },

    /**
     * Open the view product details modal.
     *
     * @method viewDetails
     *
     * @param product {Product}
     *        The product to view details of.
     *
     * @return {Promise} A promise resolved when the 'view product details' modal has been closed
     */
    viewDetails(product: ApiProduct) {
      return Modal({
        controller: 'productModalController',
        controllerAs: '$ctrl',
        template: productModalTemplate,
        productId: product.variant_parent_id || product.id,
      }).then((modal: ModalObject) => modal.closed)
    },

    /**
     * Open the AddProductFast (QUACK) modal based on given search term.
     *
     * @method quickAddProductFromSearchTerm
     *
     * @return {Promise} A promise resolved when the QUACK modal has been closed
     */
    quickAddProductBySearchTerm(searchTerm: string) {
      return Modal({
        controller: 'addProductFastModalController',
        controllerAs: '$ctrl',
        template: addProductFastModalTemplate,
        product: { name: searchTerm },
        productTypes: ['Standard', 'Variant'],
      }).then((modal: ModalObject) => modal.closed)
    },

    /**
     * Open the AddProductFast (QUACK) modal based on given product and existingVariantAttributes.
     *
     * @method quickAddVariantsToExistingProduct
     *
     * @return {Promise} A promise resolved when the QUACK modal has been closed
     */
    quickAddVariantsToExistingProduct(
      product: InitialQuickProduct,
      existingVariantAttributes: VariantAttribute[],
      existingVariantCombinationDetails: VariantAttributeCombinationDetail[],
      onBack: () => void
    ) {
      return Modal({
        controller: 'addProductFastModalController',
        controllerAs: '$ctrl',
        template: addProductFastModalTemplate,
        product,
        productTypes: ['Variant'],
        existingVariantAttributes,
        existingVariantCombinationDetails,
        onBack,
      }).then((modal: ModalObject) => modal.closed)
    },
  }
}
